import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';

export function useBadgeFirstAchievers() {
  const [firstAchievers, setFirstAchievers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const syncedRef = useRef(false);
  const syncingRef = useRef(false); // 동시 실행 방지

  // DB에서 최초 획득자 목록 fetch
  useEffect(() => {
    const fetchFirstAchievers = async () => {
      try {
        const { data, error } = await supabase
          .from('badge_first_achievers')
          .select('badge_id, player_id, player_name, achieved_at');

        if (error) {
          console.warn('badge_first_achievers 테이블 조회 실패:', error.message);
          setIsLoading(false);
          return;
        }

        const map = {};
        (data || []).forEach(row => {
          map[row.badge_id] = {
            playerId: row.player_id,
            playerName: row.player_name,
            achievedAt: row.achieved_at,
          };
        });
        setFirstAchievers(map);
      } catch (err) {
        console.warn('badge_first_achievers fetch 오류:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFirstAchievers();
  }, []);

  // 새로운 최초 획득자 sync — RPC 함수 사용 (RLS 우회)
  const syncFirstAchievers = useCallback(async (badgeMap) => {
    // 이미 sync 완료 또는 진행 중이면 스킵
    if (syncedRef.current || syncingRef.current) return;
    syncingRef.current = true; // async 시작 전에 잠금

    try {
      const toInsert = [];
      Object.entries(badgeMap).forEach(([badgeId, { players: earners }]) => {
        if (earners.length > 0 && !firstAchievers[badgeId]) {
          toInsert.push({
            badgeId,
            playerId: earners[0].id,
            playerName: earners[0].name,
          });
        }
      });

      if (toInsert.length === 0) {
        syncedRef.current = true;
        return;
      }

      const updated = { ...firstAchievers };
      for (const item of toInsert) {
        const { data, error } = await supabase.rpc('upsert_badge_first_achiever', {
          p_badge_id: item.badgeId,
          p_player_id: item.playerId,
          p_player_name: item.playerName,
        });

        if (!error && data) {
          updated[data.badge_id] = {
            playerId: data.player_id,
            playerName: data.player_name,
            achievedAt: data.achieved_at,
          };
        }
      }

      setFirstAchievers(updated);
      syncedRef.current = true; // 성공 시에만 완료 표시
    } catch (err) {
      console.warn('badge_first_achievers sync 오류:', err);
      // 실패 시 syncedRef는 false로 유지 → 재시도 가능
    } finally {
      syncingRef.current = false; // 잠금 해제
    }
  }, [firstAchievers]);

  return { firstAchievers, isLoading, syncFirstAchievers };
}
